import React, { useState, useEffect, useCallback } from 'react';
import { 
  Play, Settings, Users, BarChart3, RefreshCw, Search,
  Building2, Package, CheckCircle, XCircle, Clock, Mail
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Sidebar from '../components/layout/Sidebar';
import { demoApi, adminApi } from '../services/api';

const DemoManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [durationHours, setDurationHours] = useState(24);
  const [orgSearch, setOrgSearch] = useState('');
  const [activeTab, setActiveTab] = useState('config');

  // Fetch demo configuration
  const fetchConfig = useCallback(async () => {
    try {
      const response = await demoApi.getConfig();
      const data = response.data;
      setConfig(data);
      
      if (data.configured) {
        setSelectedOrgId(data.source_organization_id || '');
        setSelectedProductId(data.source_product_id || '');
        setIsActive(data.is_active || false);
        setDurationHours(data.demo_duration_hours || 24);
      }
    } catch (error) {
      console.error('Failed to fetch demo config:', error);
      toast.error('Failed to load demo configuration');
    }
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await demoApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch demo stats:', error);
    }
  }, []);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const response = await demoApi.getLeads();
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Failed to fetch demo leads:', error);
    }
  }, []);

  // Fetch organizations (admin only)
  const fetchOrganizations = useCallback(async () => {
    try {
      // Use admin customers API to get organizations
      const response = await adminApi.getOrganizations();
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  }, []);

  // Fetch products for selected org
  const fetchProducts = useCallback(async (orgId) => {
    if (!orgId) {
      setProducts([]);
      return;
    }
    
    try {
      const response = await adminApi.getOrganizationProducts(orgId);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchConfig(),
        fetchStats(),
        fetchLeads(),
        fetchOrganizations()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchConfig, fetchStats, fetchLeads, fetchOrganizations]);

  useEffect(() => {
    if (selectedOrgId) {
      fetchProducts(selectedOrgId);
    }
  }, [selectedOrgId, fetchProducts]);

  const handleSave = async () => {
    if (!selectedOrgId || !selectedProductId) {
      toast.error('Please select both an organization and a product');
      return;
    }

    setSaving(true);
    try {
      await demoApi.updateConfig({
        source_organization_id: selectedOrgId,
        source_product_id: selectedProductId,
        demo_duration_hours: durationHours,
        is_active: isActive
      });
      
      toast.success('Demo configuration saved');
      await fetchConfig();
    } catch (error) {
      console.error('Failed to save demo config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const filteredOrgs = organizations.filter(org => 
    org.name?.toLowerCase().includes(orgSearch.toLowerCase()) ||
    org.organization_id?.toLowerCase().includes(orgSearch.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-8 max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Play className="w-6 h-6 text-green-500" />
              Demo Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Configure the public demo experience for potential customers
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Active Demos</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.active_demos}</p>
                    </div>
                    <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <Play className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Demos</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_demos_created}</p>
                    </div>
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Leads Captured</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.leads_with_email}</p>
                    </div>
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Mail className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">This Week</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.demos_this_week}</p>
                    </div>
                    <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                      <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('config')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'config'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'leads'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Leads ({leads.length})
            </button>
          </div>

          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <Card>
              <CardHeader>
                <CardTitle>Demo Template Configuration</CardTitle>
                <CardDescription>
                  Select the organization and product that will be copied for each demo session
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <>
                    {/* Demo Status Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <Label className="text-base font-medium">Demo Active</Label>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          When enabled, visitors can start demo sessions from the landing page
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={isActive ? "default" : "secondary"}>
                          {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Switch
                          checked={isActive}
                          onCheckedChange={setIsActive}
                        />
                      </div>
                    </div>

                    {/* Source Organization */}
                    <div className="space-y-2">
                      <Label>Source Organization</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search organizations..."
                          value={orgSearch}
                          onChange={(e) => setOrgSearch(e.target.value)}
                          className="pl-10 mb-2"
                        />
                      </div>
                      <Select
                        value={selectedOrgId}
                        onValueChange={(value) => {
                          setSelectedOrgId(value);
                          setSelectedProductId('');
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an organization">
                            {selectedOrgId && (
                              <span className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                {organizations.find(o => o.organization_id === selectedOrgId)?.name || selectedOrgId}
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {filteredOrgs.map((org) => (
                            <SelectItem key={org.organization_id} value={org.organization_id}>
                              <span className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                {org.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        This organization&apos;s product will be used as the demo template
                      </p>
                    </div>

                    {/* Source Product */}
                    <div className="space-y-2">
                      <Label>Source Product</Label>
                      <Select
                        value={selectedProductId}
                        onValueChange={setSelectedProductId}
                        disabled={!selectedOrgId || products.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={
                            !selectedOrgId 
                              ? "Select an organization first" 
                              : products.length === 0 
                                ? "No products found" 
                                : "Select a product"
                          }>
                            {selectedProductId && (
                              <span className="flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                {products.find(p => p.product_id === selectedProductId)?.name || selectedProductId}
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((product) => (
                            <SelectItem key={product.product_id} value={product.product_id}>
                              <span className="flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                {product.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        This product will be deep-copied into each demo environment
                      </p>
                    </div>

                    {/* Duration */}
                    <div className="space-y-2">
                      <Label>Demo Duration (hours)</Label>
                      <Select
                        value={durationHours.toString()}
                        onValueChange={(value) => setDurationHours(parseInt(value))}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12 hours</SelectItem>
                          <SelectItem value="24">24 hours</SelectItem>
                          <SelectItem value="48">48 hours</SelectItem>
                          <SelectItem value="72">72 hours</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        Demo environments will be automatically deleted after this period
                      </p>
                    </div>

                    {/* Current Config Display */}
                    {config?.configured && (
                      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Current Configuration</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-blue-700 dark:text-blue-300">Organization:</span>
                            <span className="ml-2 text-blue-900 dark:text-blue-100">{config.source_organization_name}</span>
                          </div>
                          <div>
                            <span className="text-blue-700 dark:text-blue-300">Product:</span>
                            <span className="ml-2 text-blue-900 dark:text-blue-100">{config.source_product_name}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end pt-4">
                      <Button onClick={handleSave} disabled={saving || !selectedOrgId || !selectedProductId}>
                        {saving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save Configuration'
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <Card>
              <CardHeader>
                <CardTitle>Demo Leads</CardTitle>
                <CardDescription>
                  Visitors who started a demo session (email capture is optional)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No demo leads yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Email</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Created</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">IP Address</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-400">Converted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.map((lead) => (
                          <tr key={lead.lead_id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-3 px-4">
                              {lead.email ? (
                                <span className="text-gray-900 dark:text-white">{lead.email}</span>
                              ) : (
                                <span className="text-gray-400 italic">Not provided</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                              {formatDate(lead.created_at)}
                            </td>
                            <td className="py-3 px-4 text-gray-600 dark:text-gray-400 font-mono text-xs">
                              {lead.ip_address}
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant={lead.converted ? "default" : "secondary"}>
                                {lead.converted ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoManagementPage;
